import type {LocationQuery, LocationQueryRaw} from 'vue-router'

/**
 * Query params a paid click lands with that must survive onto the page the conversion happens
 * on (`/demo-request`). GA4 and Google Ads attribute the conversion from the URL the visitor is
 * on when the form fires, so a landing-page CTA that drops `gclid` silently breaks reporting.
 * Same list as the attribution cookie in lib/analytics/attribution.ts, kept in sync by hand.
 */
export const CAMPAIGN_CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'msclkid', 'fbclid', 'ttclid', 'li_fat_id', 'rdt_cid', 'irclickid'] as const

const UTM_PREFIX = 'utm_'

/** @return only the campaign params (`utm_*` + click ids) present in `query`. */
export function campaignParams(query: LocationQuery): LocationQueryRaw {
    const kept: LocationQueryRaw = {}
    for (const [key, value] of Object.entries(query)) {
        if (value == null || value === '') continue
        if (key.startsWith(UTM_PREFIX) || (CAMPAIGN_CLICK_ID_PARAMS as readonly string[]).includes(key)) {
            kept[key] = value
        }
    }
    return kept
}

/**
 * Build a router location for `path` that carries the campaign params from the current
 * `query`, plus any `extra` params of the link's own (e.g. `source`, `cta`). The link's own
 * params win on a name clash.
 */
export function withCampaignParams(path: string, query: LocationQuery, extra: LocationQueryRaw = {}): {path: string, query: LocationQueryRaw} {
    return {path, query: {...campaignParams(query), ...extra}}
}

/**
 * String form of {@link withCampaignParams}, for components that take a plain `to` path
 * (the marketing Button). Array values repeat the key, matching vue-router's own serialization.
 */
export function hrefWithCampaignParams(path: string, query: LocationQuery, extra: LocationQueryRaw = {}): string {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries({...campaignParams(query), ...extra})) {
        const values = Array.isArray(value) ? value : [value]
        for (const item of values) {
            if (item != null) params.append(key, String(item))
        }
    }
    const serialized = params.toString()
    return serialized ? `${path}?${serialized}` : path
}
